import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { CodingStrategyEnum, ResolvableModeEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING, TEMPORARY_STRING } from '@e-socna/empty';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useIdFactory } from '../../../hooks/useIdFactory.hook';

import type {
  ResolvableServiceForm_ResolvableServiceBaseInterface,
  ResolvableServiceForm_ResolvableServiceBaseInterfaceData,
} from '../fragments/resolvable-service-base-interface.fragment';

export const useInitialValuesFactory = (
  pInitialValues?: Readonly<ResolvableServiceForm_ResolvableServiceBaseInterfaceData>,
) => {
  const { generateId } = useIdFactory();

  const referencesContext = useReferencesContext();

  const { defaultCurrency, defaultGenericStatus, defaultVat } = ensureDefined({
    defaultCurrency: referencesContext.defaultCurrency,
    defaultGenericStatus: referencesContext.defaultGenericStatus,
    defaultUnit: referencesContext.defaultUnit,
    defaultVat: referencesContext.defaultVat,
  });

  const { codingStrategiesCodeMap } = referencesContext;

  const initialValues: ResolvableServiceForm_ResolvableServiceBaseInterface =
    useMemo(
      () => ({
        __typename: 'ResolvableService' as const,
        _id: generateId(),
        code: EMPTY_STRING,
        label: TEMPORARY_STRING,
        title: EMPTY_STRING,
        content: EMPTY_STRING,
        statusId: defaultGenericStatus._id,
        materialUsages: EMPTY_ARRAY,
        costUsages: EMPTY_ARRAY,
        ...omitBy(pInitialValues, isNil),
        codingConfig: {
          __typename: CodingStrategyEnum.CodingConfigManual as const,
          _id: generateId(),
          kind: CodingStrategyEnum.CodingConfigManual,
          strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
          code: TEMPORARY_STRING,
          ...omitBy(pInitialValues?.codingConfig, isNil),
        },
        value: {
          __typename: 'ResolvableServiceValue' as const,
          _id: generateId(),
          priceExcludingVat: {
            __typename: 'ResolvableAmount' as const,
            value: {
              __typename: 'ResolvableFloat' as const,
              mode: ResolvableModeEnum.Static,
              value: 0,
            },
            currencyId: {
              __typename: 'ResolvableId' as const,
              mode: ResolvableModeEnum.Static,
              value: defaultCurrency._id,
            },
          },
          discountPercentage: {
            __typename: 'ResolvableFloat' as const,
            mode: ResolvableModeEnum.Static,
            value: 0,
          },
          discountAmount: {
            __typename: 'ResolvableAmount' as const,
            value: {
              __typename: 'ResolvableFloat' as const,
              mode: ResolvableModeEnum.Static,
              value: 0,
            },
            currencyId: {
              __typename: 'ResolvableId' as const,
              mode: ResolvableModeEnum.Static,
              value: defaultCurrency._id,
            },
          },
          vatId: {
            __typename: 'ResolvableId' as const,
            mode: ResolvableModeEnum.Static,
            value: defaultVat._id,
          },
          ...omitBy(pInitialValues?.value, isNil),
        },
      }),
      [
        codingStrategiesCodeMap.CodingConfigManual._id,
        defaultCurrency._id,
        defaultGenericStatus._id,
        defaultVat._id,
        generateId,
        pInitialValues,
      ],
    );

  return { initialValues };
};
